'use client';

import { useEffect, useRef, type ComponentProps } from 'react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { enGB } from 'date-fns/locale/en-GB';
import {
    type CustomComponents,
    type DayButton,
    DayPicker,
    getDefaultClassNames,
} from 'react-day-picker';

import { cn } from '../../util/utils';
import { shortMonth } from '@/lib/format';
import { type Button, buttonVariants } from '../Button/Button';

/*
  react-day-picker's default locale is en-US, which starts the week on Sunday and labels a day
  "Saturday, September 5th, 2026". This admin is read in London, so the default is en-GB: the week
  starts on Monday and the label reads "Saturday, 5 September 2026". A caller can still pass its
  own `locale`, and the fields print their value through the app's own formatters either way.

  The look is the prototype's ui-date (form.css, READ 2521:19698 and 2497:22154), asked for on
  ALS-227: one month between two 24px chevrons with its name centred, a two-letter weekday row in
  the label style, 32 by 30 day cells, today in bold rather than filled, the days either side of
  the month muted, the chosen day in primary and a range's middle on the muted ground. Six rows
  always, as ui-date draws, so the popover never changes height between months. A coarse pointer
  gets 40px cells, which the design does not draw and a thumb needs.
*/
function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    fixedWeeks = true,
    captionLayout = 'label',
    buttonVariant = 'ghost',
    locale = enGB,
    formatters,
    components,
    ...props
}: ComponentProps<typeof DayPicker> & {
    buttonVariant?: ComponentProps<typeof Button>['variant'];
}) {
    const defaultClassNames = getDefaultClassNames();

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            fixedWeeks={fixedWeeks}
            className={cn(
                'bg-background group/calendar [--cell-h:30px] [--cell-w:--spacing(8)] pointer-coarse:[--cell-h:--spacing(10)] pointer-coarse:[--cell-w:--spacing(10)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
                String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
                String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
                className,
            )}
            captionLayout={captionLayout}
            locale={locale}
            formatters={{
                formatMonthDropdown: shortMonth,
                // "Mo", whatever length the locale's own short name is.
                formatWeekdayName: (date) => WEEKDAYS[date.getDay()],
                ...formatters,
            }}
            classNames={{
                root: cn('w-fit', defaultClassNames.root),
                months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
                month: cn('flex w-full flex-col gap-2', defaultClassNames.month),
                nav: cn(
                    'absolute inset-x-0 top-0 flex w-full items-center justify-between',
                    defaultClassNames.nav,
                ),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'text-muted-foreground size-6 rounded-sm p-0 select-none aria-disabled:opacity-50 pointer-coarse:size-10',
                    defaultClassNames.button_previous,
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'text-muted-foreground size-6 rounded-sm p-0 select-none aria-disabled:opacity-50 pointer-coarse:size-10',
                    defaultClassNames.button_next,
                ),
                month_caption: cn(
                    'mb-0.5 flex h-6 w-full items-center justify-center px-8 pointer-coarse:h-10',
                    defaultClassNames.month_caption,
                ),
                dropdowns: cn(
                    'flex h-6 w-full items-center justify-center gap-1.5 text-sm font-medium',
                    defaultClassNames.dropdowns,
                ),
                dropdown_root: cn(
                    'has-focus:border-ring border-input has-focus:ring-outline relative rounded-md border shadow-xs has-focus:ring-[3px]',
                    defaultClassNames.dropdown_root,
                ),
                dropdown: cn('bg-popover absolute inset-0 opacity-0', defaultClassNames.dropdown),
                caption_label: cn(
                    'text-sm font-medium whitespace-nowrap select-none',
                    captionLayout === 'label'
                        ? ''
                        : '[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-md pr-1 pl-2 [&>svg]:size-3.5',
                    defaultClassNames.caption_label,
                ),
                table: 'border-collapse',
                // 8px between the weekday row and the days, as ui-date__week and __grid sit.
                weekdays: cn('flex pb-2', defaultClassNames.weekdays),
                weekday: cn(
                    'text-label-sm text-muted-foreground flex h-6 w-(--cell-w) items-center justify-center select-none',
                    defaultClassNames.weekday,
                ),
                week: cn('flex', defaultClassNames.week),
                week_number_header: cn(
                    'w-(--cell-w) select-none',
                    defaultClassNames.week_number_header,
                ),
                week_number: cn(
                    'text-muted-foreground text-[0.8rem] select-none',
                    defaultClassNames.week_number,
                ),
                day: cn(
                    'group/day relative h-(--cell-h) w-(--cell-w) p-0 text-center select-none',
                    defaultClassNames.day,
                ),
                range_start: cn(defaultClassNames.range_start),
                range_middle: cn('bg-muted', defaultClassNames.range_middle),
                range_end: cn(defaultClassNames.range_end),
                // Bold on the day button, not here: see data-today below.
                today: cn(defaultClassNames.today),
                outside: cn('text-muted-foreground opacity-55', defaultClassNames.outside),
                disabled: cn('text-muted-foreground opacity-40', defaultClassNames.disabled),
                hidden: cn('invisible', defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                Root: CalendarRoot,
                Chevron: CalendarChevron,
                DayButton: CalendarDayButton,
                WeekNumber: CalendarWeekNumber,
                ...components,
            }}
            {...props}
        />
    );
}

/** The weekday row, two letters each, Sunday first as `Date.getDay` counts. */
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function CalendarDayButton({
    className,
    day,
    modifiers,
    ...props
}: React.ComponentProps<typeof DayButton>) {
    const defaultClassNames = getDefaultClassNames();

    const ref = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (modifiers.focused) ref.current?.focus();
    }, [modifiers.focused]);

    return (
        <button
            ref={ref}
            type="button"
            data-day={day.date.toLocaleDateString()}
            data-selected-single={
                modifiers.selected &&
                !modifiers.range_start &&
                !modifiers.range_end &&
                !modifiers.range_middle
            }
            data-range-start={modifiers.range_start}
            data-range-end={modifiers.range_end}
            data-range-middle={modifiers.range_middle}
            /* Today in bold, once. With two months showing, today is also one of the next
               month's leading days, and bolding that copy too drew it twice (ALS-56, fixed in
               ALS-273). The prototype does the same; it is an edge its grid did not handle. */
            data-today={modifiers.today && !modifiers.outside}
            className={cn(
                'focus-visible:ring-outline flex size-full items-center justify-center rounded-sm text-[13px] leading-none outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-[3px] disabled:pointer-events-none',
                'hover:bg-muted data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-transparent data-[today=true]:font-semibold',
                'data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground',
                defaultClassNames.day,
                className,
            )}
            {...props}
        />
    );
}

export { Calendar, CalendarDayButton };

/* The day picker's own slots, as module-level components so they keep their identity across
   renders (an inline component remounts on every render and drops focus). */
type Slots = Required<CustomComponents>;

function CalendarRoot({ className, rootRef, ...props }: ComponentProps<Slots['Root']>) {
    return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />;
}

function CalendarChevron({ className, orientation, ...props }: ComponentProps<Slots['Chevron']>) {
    if (orientation === 'left') {
        return <ChevronLeftIcon className={cn('size-[15px]', className)} {...props} />;
    }
    if (orientation === 'right') {
        return <ChevronRightIcon className={cn('size-[15px]', className)} {...props} />;
    }
    return <ChevronDownIcon className={cn('size-4', className)} {...props} />;
}

function CalendarWeekNumber({ children, ...props }: ComponentProps<Slots['WeekNumber']>) {
    return (
        <td {...props}>
            <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
            </div>
        </td>
    );
}
